<template>
  <div class="space-center register">
    <!-- 标题 -->
    <h2 class="register-title">注册账号</h2>

    <!-- 注册表单 -->
    <div class="form">
      <!-- 用户名输入框 -->
      <div class="input-group" :class="{ 'has-error': touched.username && errors.username }">
        <input
          ref="usernameInputRef"
          v-model="formData.username"
          type="text"
          placeholder="请输入用户名"
          class="main-input"
          @blur="markTouched('username'); checkUsername()"
          @input="checkUsername"
        />
        <div class="svg">
          <svg-icon name="用户" color="svg" />
        </div>
        <div v-if="touched.username && errors.username" class="error-message">
          {{ errors.username }}
        </div>
      </div>

      <!-- 手机号输入框 -->
      <div class="input-group" :class="{ 'has-error': touched.phone && errors.phone }">
        <input
          ref="phoneInputRef"
          v-model="formData.phone"
          type="tel"
          placeholder="请输入手机号"
          class="main-input"
          maxlength="11"
          @blur="markTouched('phone'); checkPhone()"
          @input="checkPhone"
        />
        <div class="svg">
          <svg-icon name="手机" color="svg" />
        </div>
        <div v-if="touched.phone && errors.phone" class="error-message">
          {{ errors.phone }}
        </div>
      </div>

      <!-- 密码输入框 -->
      <div class="input-group" :class="{ 'has-error': touched.password && errors.password }">
        <input
          ref="passwordInputRef"
          v-model="formData.password"
          :type="showPassword ? 'text' : 'password'"
          placeholder="请输入密码（6-20位）"
          class="main-input"
          autocorrect="off"
          autocapitalize="off"
          @blur="markTouched('password'); checkPassword()"
          @input="checkPassword"
        />
        <div class="svg password-icon" @click="togglePassword">
          <svg-icon :name="showPassword ? '解锁' : '密码'" color="svg" />
        </div>
        <div v-if="touched.password && errors.password" class="error-message">
          {{ errors.password }}
        </div>
      </div>

      <!-- 图形验证码 -->
      <div class="input-group captcha-group" :class="{ 'has-error': touched.captcha && errors.captcha }">
        <input
          ref="captchaInputRef"
          v-model="formData.captcha"
          type="text"
          placeholder="请输入图形验证码"
          class="main-input"
          maxlength="6"
          @blur="markTouched('captcha'); checkCaptcha()"
          @input="checkCaptcha"
        />
        <div class="captcha-img" @click="refreshCaptcha" title="点击刷新">
          <img v-if="captchaImgUrl" :src="captchaImgUrl" alt="验证码" />
          <div v-else class="captcha-placeholder">{{ captchaText }}</div>
        </div>
        <div v-if="touched.captcha && errors.captcha" class="error-message">
          {{ errors.captcha }}
        </div>
      </div>

      <!-- 短信验证码 -->
      <div class="input-group sms-group" :class="{ 'has-error': touched.smsCode && errors.smsCode }">
        <input
          ref="smsCodeInputRef"
          v-model="formData.smsCode"
          type="text"
          placeholder="请输入短信验证码"
          class="main-input"
          maxlength="6"
          @blur="markTouched('smsCode'); checkSmsCode()"
          @input="checkSmsCode"
        />
        <button
          class="send-sms-btn"
          :disabled="smsCooldown > 0 || !canSendSms || isTacLoading"
          @click="sendSmsCode"
        >
          {{ smsCooldown > 0 ? `${smsCooldown}s` : isTacLoading ? '校验中...' : '发送验证码' }}
        </button>
        <div id="captcha-box-register" class="tac-container"></div>
        <div v-if="touched.smsCode && errors.smsCode" class="error-message">
          {{ errors.smsCode }}
        </div>
      </div>

      <!-- 注册按钮 -->
      <button class="Signup-Btn register-button" :disabled="loading" @click="handleSubmit">
        <svg-icon v-if="loading" class="icon-loading" size="18" name="加载" color="#fff" />
        <span class="button-text">{{ loading ? '注册中...' : '注册' }}</span>
      </button>

      <!-- 返回登录 -->
      <button class="back-btn" @click="goToLogin">返回登录</button>
    </div>

    <dark />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'd3components/src/utils/tools'
import { getLuatCaptcha, getSendCode, showLuatTacCaptcha } from '../api'
import { ApiUserLuat } from 'd3components'
import dark from '@/layout/components/dark.vue'

const { luatRegisterUser } = ApiUserLuat

defineOptions({ name: 'luatos-register' })

const router = useRouter()

// ===== 输入框引用 =====
const usernameInputRef = ref<HTMLInputElement | null>(null)
const phoneInputRef = ref<HTMLInputElement | null>(null)
const passwordInputRef = ref<HTMLInputElement | null>(null)
const captchaInputRef = ref<HTMLInputElement | null>(null)
const smsCodeInputRef = ref<HTMLInputElement | null>(null)

// ==================== 状态管理 ====================
const loading = ref(false)
const showPassword = ref(false)
const isTacLoading = ref(false)
const captchaId = ref('')
const captchaImgUrl = ref('')
const captchaText = ref('M4B6')
const smsCooldown = ref(0)

// 字段触摸状态（用于智能提示）
const touched = ref({
  username: false,
  phone: false,
  password: false,
  captcha: false,
  smsCode: false,
})

// 字段错误信息
const errors = ref({
  username: '',
  phone: '',
  password: '',
  captcha: '',
  smsCode: '',
})

let cooldownTimer: any = null
let currentTac: any = null

// 表单数据
const formData = ref({
  username: '',
  phone: '',
  password: '',
  captcha: '',
  smsCode: '',
})

// ==================== 计算属性 ====================
// 是否可以发送短信验证码
const canSendSms = computed(() => {
  return formData.value.phone && 
         /^1[3456789]\d{9}$/.test(formData.value.phone) && 
         formData.value.captcha
})

// ==================== 字段验证（实时） ====================
/**
 * 标记字段为已触摸
 */
const markTouched = (field: keyof typeof touched.value) => {
  touched.value[field] = true
}

/**
 * 验证用户名（实时）
 */
const checkUsername = () => {
  if (!touched.value.username) return
  
  if (!formData.value.username) {
    errors.value.username = '请输入用户名'
  } else if (formData.value.username.length < 2) {
    errors.value.username = '用户名至少2个字符'
  } else if (formData.value.username.length > 20) {
    errors.value.username = '用户名最多20个字符'
  } else {
    errors.value.username = ''
  }
}

/**
 * 验证手机号（实时）
 */
const checkPhone = () => {
  if (!touched.value.phone) return
  
  if (!formData.value.phone) {
    errors.value.phone = '请输入手机号'
  } else if (!/^1[3456789]\d{9}$/.test(formData.value.phone)) {
    errors.value.phone = '请输入正确的手机号'
  } else {
    errors.value.phone = ''
  }
}

/**
 * 验证密码（实时）
 */
const checkPassword = () => {
  if (!touched.value.password) return
  
  if (!formData.value.password) {
    errors.value.password = '请输入密码'
  } else if (formData.value.password.length < 6) {
    errors.value.password = '密码至少6位'
  } else if (formData.value.password.length > 20) {
    errors.value.password = '密码最多20位'
  } else {
    errors.value.password = ''
  }
}

/**
 * 验证图形验证码（实时）
 */
const checkCaptcha = () => {
  if (!touched.value.captcha) return
  
  if (!formData.value.captcha) {
    errors.value.captcha = '请输入图形验证码'
  } else if (formData.value.captcha.length < 4) {
    errors.value.captcha = '图片验证码错误'
  } else {
    errors.value.captcha = ''
  }
}

/**
 * 验证短信验证码（实时）
 */
const checkSmsCode = () => {
  if (!touched.value.smsCode) return
  
  if (!formData.value.smsCode) {
    errors.value.smsCode = '请输入短信验证码'
  } else if (formData.value.smsCode.length < 6) {
    errors.value.smsCode = '请输入正确格式验证码'
  } else {
    errors.value.smsCode = ''
  }
}

// ==================== 页面导航 ====================
/**
 * 返回登录页面
 */
const goToLogin = () => {
  router.push('/login')
}

// ==================== 密码显示切换 ====================
/**
 * 切换密码可见性
 */
const togglePassword = () => {
  showPassword.value = !showPassword.value
}

// ==================== 图形验证码 ====================
/**
 * 刷新图形验证码
 */
const refreshCaptcha = async () => {
  try {
    const res = await getLuatCaptcha()
    if (res.code==0) {
      captchaImgUrl.value = res.value.img
      captchaId.value = res.value.captcha_id;
    } else {
      toast(res?.msg || '获取验证码失败', 3)
    }
  } catch (err: any) {
    toast(err?.message || '获取验证码异常', 3)
  }
}

// ==================== 短信验证码 ====================
/**
 * 发送短信验证码前的验证
 */
const validateBeforeSendSms = (): boolean => {
  // 标记字段为已触摸
  touched.value.phone = true
  touched.value.captcha = true
  
  // 触发验证
  checkPhone()
  checkCaptcha()
  
  // 检查是否有错误
  if (errors.value.phone) {
    toast(errors.value.phone, 3)
    return false
  }
  
  if (errors.value.captcha) {
    toast(errors.value.captcha, 3)
    return false
  }
  
  return true
}

/**
 * 启动倒计时
 */
const startCooldown = () => {
  smsCooldown.value = 60
  
  // 清除旧计时器
  if (cooldownTimer) {
    clearInterval(cooldownTimer)
    cooldownTimer = null
  }
  
  // 开始新的倒计时
  cooldownTimer = setInterval(() => {
    smsCooldown.value--
    if (smsCooldown.value <= 0) {
      clearInterval(cooldownTimer)
      cooldownTimer = null
    }
  }, 1000)
}

/**
 * 发送短信验证码
 */
const sendSmsCode = async () => {
  // 验证前置条件
  if (!validateBeforeSendSms()) return
  
  // 防止重复点击
  if (smsCooldown.value > 0 || isTacLoading.value) return

  isTacLoading.value = true
  currentTac?.destroyWindow?.()

  await showLuatTacCaptcha({
    bindEl: '#captcha-box-register',
    phone: formData.value.phone,
    onSuccess: async (response: any) => {
      try {
        const res = await getSendCode({
          captchaId: captchaId.value,
          phone: formData.value.phone,
          captchaCode: formData.value.captcha,
          auth2: response?.auth2,
        })
        
        if (res?.isSuc || res?.code === 0) {
          toast('验证码已发送，请注意查收', 1)
          startCooldown()
        } else {
          toast(res?.obj || res?.value || res?.msg || '发送验证码失败', 2)
          refreshCaptcha()
        }
      } catch (err: any) {
        toast(err?.message || '发送验证码异常', 3)
        refreshCaptcha()
      } finally {
        isTacLoading.value = false
        currentTac = null
      }
    },
    onClose: () => {
      isTacLoading.value = false
      currentTac = null
    },
    onLoadError: (err: any) => {
      toast(err?.message || '图形验证码加载失败', 3)
      isTacLoading.value = false
      currentTac = null
    },
  }).then((tac: any) => {
    currentTac = tac
  }).catch(() => {})
}

// ==================== 表单提交验证 ====================
/**
 * 提交前验证所有字段
 */
const validateAllFields = (): boolean => {
  // 按顺序检查每个字段，遇到第一个错误就停止
  
  // 检查用户名
  if (!touched.value.username) touched.value.username = true
  checkUsername()
  if (errors.value.username) {
    toast(errors.value.username, 3)
    nextTick(() => usernameInputRef.value?.focus())
    return false
  }
  
  // 检查手机号
  if (!touched.value.phone) touched.value.phone = true
  checkPhone()
  if (errors.value.phone) {
    toast(errors.value.phone, 3)
    nextTick(() => phoneInputRef.value?.focus())
    return false
  }
  
  // 检查密码
  if (!touched.value.password) touched.value.password = true
  checkPassword()
  if (errors.value.password) {
    toast(errors.value.password, 3)
    nextTick(() => passwordInputRef.value?.focus())
    return false
  }
  
  // 检查图形验证码
  if (!touched.value.captcha) touched.value.captcha = true
  checkCaptcha()
  if (errors.value.captcha) {
    toast(errors.value.captcha, 3)
    nextTick(() => captchaInputRef.value?.focus())
    return false
  }
  
  // 检查短信验证码
  if (!touched.value.smsCode) touched.value.smsCode = true
  checkSmsCode()
  if (errors.value.smsCode) {
    toast(errors.value.smsCode, 3)
    nextTick(() => smsCodeInputRef.value?.focus())
    return false
  }
  
  return true
}

// ==================== 注册提交 ====================
/**
 * 提交注册表单
 */
const handleSubmit = async () => {
  if (!validateAllFields()) return

  loading.value = true
  
  try {
    const response = await luatRegisterUser({
      name: formData.value.username,
      phone: formData.value.phone,
      password: formData.value.password,
      vcode: formData.value.smsCode,
    })
    
    if (response.code==0) {
      toast('注册成功', 1)
      setTimeout(() => router.push('/login'), 1500)
    } else {
      toast(response.value || '注册失败', 2)
    }
  } catch (err: any) {
    toast(err?.value || '注册失败，请重试', 3)
  } finally {
    loading.value = false
  }
}

// ==================== 生命周期 ====================
onMounted(() => {
  refreshCaptcha()
  
  // ===== 自动聚焦第一个输入框 =====
  usernameInputRef.value?.focus()
})

onUnmounted(() => {
  if (cooldownTimer) {
    clearInterval(cooldownTimer)
    cooldownTimer = null
  }
  currentTac?.destroyWindow?.()
  currentTac = null
})
</script>

<style scoped lang="scss">

$primary-color-login-text: rgba(57, 140, 254, 1);

// ===== 科技感配色 =====
$primary-gradient: linear-gradient(135deg, rgba(57, 140, 254, 1) 0%, rgba(26, 111, 220, 1) 100%);
// $primary-gradient: $primary-color-login-text;
 //$accent-color: ;
$accent-color: $primary-color-login-text;
// 使用与登录页面相同的样式变量
// $primary-gradient: var(--color-primary);
// $accent-color: var(--color-primary);
$text-secondary: #8a94a6;
$spacing-md: 1.25rem;
$spacing-lg: 1.5rem;
$spacing-xl: 1.875rem;
$radius-sm: 4px;
$radius-full: 54px;
$shadow-xs: 0 2px 8px rgba(0, 0, 0, 0.02);
$shadow-md: 0 4px 16px rgba(57, 140, 254, 0.3);
$shadow-lg: 0 6px 20px rgba(57, 140, 254, 0.45);
$shadow-hover: 0 4px 16px rgba($accent-color, 0.08);
$shadow-focus:
  0 0 0 4px rgba($accent-color, 0.1),
  0 6px 20px rgba($accent-color, 0.12);
$font-size-lg: 1rem;
$font-weight-medium: 500;
$font-weight-semibold: 600;
$font-weight-bold: 900;
$transition-base: 0.3s ease;
$transition-md: 0.35s cubic-bezier(0.4, 0, 0.2, 1);
$transition-lg: 0.4s cubic-bezier(0.4, 0, 0.2, 1);
$transition-xl: 0.5s ease;

.register {
  width: 100vw;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
 background-color: #fff !important;
  h2 {
    background: $primary-gradient;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: relative;
    letter-spacing: 0.1em;
  }

  .register-title {
    font-weight: $font-weight-bold;
    //margin-bottom: 1.3rem;
  }

  :deep(.form) {
    padding: $spacing-lg 0;
    width: 80%;

    .input-group {
      position: relative;
      margin-top: $spacing-xl;
      background: #fafbfc;
      border: 2px solid #e8ecf2 !important;
      border-radius: $radius-full;
      padding: 14px 54px;
      transition: all $transition-md;
      box-shadow: $shadow-xs;
      outline: none;

      &:hover {
        border-color: rgba($accent-color, 0.4);
        background: #fff;
        box-shadow: $shadow-hover;
      }

      &:focus-within {
        border-color: $accent-color !important;
        background: #fff;
        box-shadow: $shadow-focus;

        .svg {
          opacity: 1;
          color: $accent-color;
        }
      }

      // 错误状态样式
      &.has-error {
        border-color: #f56c6c !important;
        background: #fef0f0;

        &:hover {
          border-color: #f56c6c !important;
          background: #fef0f0;
        }

        &:focus-within {
          border-color: #f56c6c !important;
          background: #fef0f0;
          box-shadow: 0 0 0 4px rgba(245, 108, 108, 0.1);

          .svg {
            color: #f56c6c;
          }
        }

        .svg {
          color: #f56c6c;
        }
      }

      // 错误提示文字（绝对定位，不占据高度）
      .error-message {
        position: absolute;
        left: 54px;
        right: 54px;
        top: 100%;
        margin-top: 8px;
        font-size: 24px;
        color: #f56c6c;
        line-height: 1.2;
        font-weight: 500;
        animation: slideDown 0.3s ease;
        z-index: 10;
      }

      .main-input {
        padding: 0.5rem 0.5rem 0.5rem 0.25rem;
        border: none;
        background: transparent !important;
        font-size: $font-size-lg;
        font-weight: $font-weight-medium;
        color: #2d3748;
        width: 100%;
        transition: all $transition-base;

        &::placeholder {
          color: $text-secondary;
        }
        &:focus {
          outline: none;
        }
      }

      .svg {
        position: absolute;
        right: 0.9rem;
        top: 45%;
        transform: translateY(-50%);
        font-size: 1.5rem;
        color: $accent-color;
        opacity: 0.7;
        transition: all $transition-md;

        &.password-icon {
          cursor: pointer;
          &:hover {
            opacity: 1;
            transform: translateY(-50%) scale(1.1);
          }
        }
      }
    }

    .password-group {
      margin-top: $spacing-lg;
    }

    // 图形验证码行：input 占左侧，验证码图片在右侧
    .captcha-group {
      padding-right: 0;
      display: flex;
      align-items: center;

      .main-input {
        flex: 1;
        padding-right: 0.5rem;
      }

      .captcha-img {
        flex-shrink: 0;
        width: 130px;
        height: 100%;
        cursor: pointer;
        border-left: 2px solid #e8ecf2;
        // overflow: hidden;
        display: flex;
        margin-right: 20px;
        align-items: center;
        justify-content: center;
        border-radius: 0 $radius-full $radius-full 0;
        transition: opacity $transition-base;

        &:hover {
          opacity: 0.8;
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .captcha-placeholder {
          font-family: 'Courier New', monospace;
          font-size: 1.15rem;
          font-weight: $font-weight-bold;
          letter-spacing: 0.25em;
          // 彩色渐变文字
          background: linear-gradient(120deg, #3a7bd5 0%, #e96c5a 40%, #3a7bd5 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          // 背景：网格噪点质感
          background-color: #eef5ff;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          user-select: none;
          position: relative;
          // 模拟噪点纹理
          background-image:
            linear-gradient(120deg, #3a7bd5 0%, #e96c5a 40%, #3a7bd5 100%),
            repeating-linear-gradient(
              45deg,
              rgba(57, 123, 213, 0.04) 0px,
              rgba(57, 123, 213, 0.04) 1px,
              transparent 1px,
              transparent 8px
            );
          text-shadow: 1px 1px 2px rgba(255, 255, 255, 0.6), -1px -1px 2px rgba(0, 0, 0, 0.08);
        }
      }
    }

    // 短信验证码行：input 占左侧，发送按钮在右侧
    .sms-group {
      padding-right: 0;
      display: flex;
      align-items: center;

      .main-input {
        flex: 1;
        padding-right: 0.5rem;
      }

      .send-sms-btn {
        flex-shrink: 0;
        height: 100%;
        min-width: 110px;
        padding: 0 12px;
        background: transparent;
        color: $accent-color;
        border: none;
        border-left: 2px solid #e8ecf2;
        border-radius: 0 $radius-full $radius-full 0;
        font-size: 0.85rem;
        font-weight: $font-weight-semibold;
        cursor: pointer;
        transition: all $transition-base;
        white-space: nowrap;
        letter-spacing: 0.02em;

        &:hover:not(:disabled) {
          background: rgba($accent-color, 0.06);
        }

        &:disabled {
          color: #b0b8c4;
          cursor: not-allowed;
        }
      }

      .tac-container {
        position: absolute;
        right: 0;
        bottom: calc(100% + 8px);
        z-index: 9999;
        background: transparent;
        transform-origin: right bottom;
      }
    }

    .Signup-Btn {
            user-select: none;
      &.register-button {
        margin-top: $spacing-xl;
      }

      background: $primary-gradient;
      border: none;
      padding: 0 $spacing-lg;
      height: 88px;
      min-height: 88px;
      font-size: $font-size-lg;
      font-weight: $font-weight-semibold;
      letter-spacing: 0.15em;
      box-shadow: $shadow-md;
      transition: all $transition-lg;
      position: relative;
      overflow: hidden;
      border-radius: $radius-full;
      color: #fff;
      width: 100%;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transition: left $transition-xl;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: $shadow-lg;

        &::before {
          left: 100%;
        }
      }

      &:active {
        transform: translateY(0) scale(0.98);
      }

      .button-text {
        margin-left: 10px;
      }
    }

    .back-btn {
      user-select: none;
      margin-top: $spacing-md;
      width: 100%;
      height: 88px;
      border: 2px solid $accent-color;
      background: transparent;
      color: $accent-color;
      font-size: $font-size-lg;
      font-weight: $font-weight-semibold;
      letter-spacing: 0.15em;
      border-radius: $radius-full;
      cursor: pointer;
      transition: all $transition-base;

      &:hover {
        background: rgba($accent-color, 0.1);
        transform: translateY(-2px);
      }

      &:active {
        transform: translateY(0) scale(0.98);
      }
    }
  }
}

:global(#tianai-captcha-parent) {
  background: #fff;
}
</style>
